import { useAutoAnimate } from "@formkit/auto-animate/react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ReactComponent as TrashIcon } from "../../assets/icons/tables/trash-icon.svg";
import { FormSelect, PrimaryButton } from "../../components";
import { usePartners } from "../../store/usePartners";
import { IVoucher, useVouchers } from "../../store/useVouchers";
import { withProtectedRoute } from "../../utils/HOC/withProtectedRoute";
import { LotteryVoucherModal } from "./components/LotteryVoucherModal";
import { useLottery, ILottery } from "../../store/useLottery";
import { useAlert } from "../../store/useAlert";

dayjs.extend(relativeTime);

function LotteryVouchersScreen() {
  const navigate = useNavigate();

  const { allPartners, getPartnersForSelect } = usePartners();

  const { allVouchers, hasNextPage, getAllVouchers, addManyVoucher, deleteVoucher } = useVouchers();
  const { allLotteries, getAllLotteries } = useLottery();
  const { setAlert } = useAlert();
  const [animationParent] = useAutoAnimate();

  const [selectedPartnerId, setSelectedPartnerId] = useState<string>("");
  const [bulkPartnerId, setBulkPartnerId] = useState<string>("");
  const [bulkLotteryId, setBulkLotteryId] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    getPartnersForSelect();
    if (!allLotteries.length) {
      getAllLotteries(1, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If a partner is selected, load that partner's vouchers; otherwise load all vouchers
    if (selectedPartnerId) {
      getAllVouchers(1, 25, selectedPartnerId);
    } else {
      getAllVouchers(1, 25);
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartnerId]);

  const lotteryVouchers: IVoucher[] = useMemo(
    () =>
      allVouchers.filter((voucher) => {
        if (voucher.sourceType === "LOTTERY") return true;
        if (!voucher.sourceType && voucher.lottery) return true;
        return false;
      }),
    [allVouchers]
  );

  const selectedPartnerName =
    allPartners.find((p) => p._id === selectedPartnerId)?.name ??
    "All partners";

  const bulkPartnerLotteries: ILottery[] = useMemo(() => {
    if (!bulkPartnerId) return [];
    return allLotteries.filter((lottery) => {
      const partner =
        typeof lottery.partner === "string"
          ? lottery.partner
          : (lottery.partner as any)?._id;
      return partner === bulkPartnerId;
    });
  }, [allLotteries, bulkPartnerId]);

  const parseBooleanCell = (value: any): boolean | undefined => {
    if (value === undefined || value === null || value === "") return undefined;
    if (typeof value === "boolean") return value;
    const normalized = String(value).trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
    return undefined;
  };

  const processBulkRows = async (rows: any[]) => {
    const onlyData = rows.slice(1);
    const payload = onlyData
      .filter((row) => Array.isArray(row) && row.length > 0 && String(row[0] ?? "").trim())
      .map((row) => ({
        code: String(row[0]).trim(),
        value: row[1] ? String(row[1]) : undefined,
        description: row[2] ? String(row[2]) : undefined,
        instructions: row[3] ? String(row[3]) : undefined,
        benefitText: row[4] ? String(row[4]) : undefined,
        expireDate: row[5] ? String(row[5]) : undefined,
        allowMultipleRedemptions: parseBooleanCell(row[6]),
        partnerId: bulkPartnerId,
        lottery: bulkLotteryId,
        sourceType: "LOTTERY" as const,
      }));

    if (!payload.length) {
      setAlert({
        isOpen: true,
        message: "No valid voucher rows found in file.",
        severity: "error",
      });
      return;
    }

    setIsBulkUploading(true);
    try {
      await addManyVoucher(payload as any);
      setAlert({
        isOpen: true,
        message: `Uploaded ${payload.length} lottery vouchers.`,
        severity: "success",
      });
      handleVoucherCreated();
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message:
          error?.response?.data?.message ??
          "Bulk upload failed. Please check your file format.",
        severity: "error",
      });
    } finally {
      setIsBulkUploading(false);
      setFileInputKey((prev) => prev + 1);
    }
  };

  const handleBulkFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!bulkPartnerId || !bulkLotteryId) {
      setAlert({
        isOpen: true,
        message: "Please select both partner and lottery before file upload.",
        severity: "error",
      });
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (extension === "csv") {
      Papa.parse(file, {
        complete: (result: any) => processBulkRows(result.data || []),
      });
      return;
    }

    if (extension === "xlsx" || extension === "xls") {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const allRows: any[] = [];
        workbook.SheetNames.forEach((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          allRows.push(...rows);
        });
        processBulkRows(allRows);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    setAlert({
      isOpen: true,
      message: "Unsupported file format. Use CSV or Excel.",
      severity: "error",
    });
  };

  const loadMore = () => {
    if (!hasNextPage) return;
    const nextPage = page + 1;
    setPage(nextPage);
    if (selectedPartnerId) {
      getAllVouchers(nextPage, 25, selectedPartnerId);
    } else {
      getAllVouchers(nextPage, 25);
    }
  };

  const handleVoucherCreated = () => {
    if (selectedPartnerId) {
      getAllVouchers(1, 25, selectedPartnerId);
    } else {
      getAllVouchers(1, 25);
    }
    setPage(1);
  };

  return (
    <div>
      <div className="flex flex-row items-center justify-between mb-6">
        <div>
          <h1 className="font-satoshi text-2xl font-bold text-dark">
            Lottery vouchers
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            View and manage vouchers that are used as rewards for lotteries.
            Start by selecting a partner to see their lottery vouchers.
          </p>
        </div>
        <PrimaryButton
          label="Create voucher"
          onClick={() => setIsModalOpen(true)}
          className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
        />
      </div>

      <LotteryVoucherModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        partnerId={selectedPartnerId}
        partnerName={selectedPartnerName}
        onCreated={handleVoucherCreated}
      />

      <div className="bg-white rounded-xl shadow-none drop-shadow-none border-gray-border mb-4">
        {true && (
        <div className="w-full border-b border-gray-border border-l-4 border-l-[#FF6E41] px-6 py-4">
          <p className="font-satoshi font-medium text-base text-dark">
            Add many (lottery vouchers)
          </p>
          <p className="text-xs text-gray-600 mt-1 mb-3">
            Select partner and lottery first, then upload CSV/Excel.
            Format: Code, Value, Description, Instructions, Benefit Text, Expire Date (dd-mm-yyyy), Allow Multiple Redeems (true/false).
          </p>
          <a
            href="/voucher-upload-template.csv"
            download="voucher-upload-template.csv"
            className="inline-flex items-center mb-3 rounded-md border border-[#FF6E41] bg-[#FF6E41]/10 px-3 py-1.5 text-xs font-satoshi font-semibold text-[#FF6E41] hover:bg-[#FF6E41]/15 transition-colors"
          >
            Download example CSV
          </a>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Partner *</label>
              <FormSelect
                value={bulkPartnerId}
                onChange={(e: any) => {
                  setBulkPartnerId(e.target.value);
                  setBulkLotteryId("");
                }}
                options={[
                  { value: "", label: "Select partner..." },
                  ...allPartners
                    .filter((partner) => !partner?.isCourier)
                    .map((partner) => ({
                    value: partner._id as string,
                    label: partner.name as string,
                  })),
                ]}
                placeholder="Select partner..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lottery *</label>
              <FormSelect
                value={bulkLotteryId}
                onChange={(e: any) => setBulkLotteryId(e.target.value)}
                options={[
                  { value: "", label: "Select lottery..." },
                  ...bulkPartnerLotteries.map((lottery) => ({
                    value: lottery._id,
                    label: lottery.en_title || lottery.nl_title,
                  })),
                ]}
                placeholder="Select lottery..."
              />
            </div>
            <div className="flex justify-start sm:justify-end">
              <input
                key={fileInputKey}
                id="bulk-lottery-vouchers-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                className="hidden"
                onChange={handleBulkFileUpload}
              />
              <PrimaryButton
                label={isBulkUploading ? "Uploading..." : "Add many"}
                onClick={() => {
                  (document.getElementById("bulk-lottery-vouchers-input") as HTMLInputElement | null)?.click();
                }}
                disabled={isBulkUploading || !bulkPartnerId || !bulkLotteryId}
              />
            </div>
          </div>
        </div>
        )}
        <div className="w-full border-b border-gray-border px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="font-satoshi font-medium text-base text-dark">
              Partner selection
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Choose a partner to filter vouchers, or leave it empty to see all
              lottery vouchers.
            </p>
          </div>
          <div className="min-w-[220px]">
            <FormSelect
              value={selectedPartnerId}
              onChange={(e: any) => {
                setSelectedPartnerId(e.target.value);
              }}
              options={[
                { value: "", label: "All partners" },
                ...allPartners
                    .filter((partner) => !partner?.isCourier)
                    .map((partner) => ({
                  value: partner._id as string,
                  label: partner.name as string,
                })),
              ]}
              placeholder="All partners"
            />
          </div>
        </div>

        <div className="w-full px-6 py-4">
          {!lotteryVouchers.length ? (
            <p className="text-sm text-gray-600">
              {selectedPartnerId ? (
                <>
                  There are no lottery vouchers for{" "}
                  <strong>{selectedPartnerName}</strong> yet.
                </>
              ) : (
                "There are no lottery vouchers yet."
              )}
            </p>
          ) : (
            <div className="relative overflow-x-auto">
              <table className="w-full text-base font-normal font-satoshi text-left rtl:text-right text-dark">
                <thead className="font-medium bg-gray-50">
                  <tr>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Code</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Partner</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Lottery</th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3">Value</th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Instructions
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Benefit text
                    </th>
                    <th className="hidden min-w-48 sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Expire date
                    </th>
                    <th className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                      Status
                    </th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                      Redeemed
                    </th>
                    <th className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody ref={animationParent}>
                  {lotteryVouchers.map((voucher) => (
                    <tr
                      key={voucher._id}
                      onClick={() =>
                        voucher._id &&
                        navigate(`/voucher-details/${voucher._id}`)
                      }
                      className="bg-white rounded-xl shadow-none drop-shadow-none border-gray-border hover:bg-gray-100 transition ease-linear border-b cursor-pointer"
                    >
                      <td className="px-2 sm:px-6 py-2 sm:py-3 break-all sm:whitespace-nowrap">
                        {voucher.code}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3 whitespace-nowrap">
                        {typeof voucher.partnerId === "object" &&
                        (voucher.partnerId as any)
                          ? (voucher.partnerId as any)?.name ?? "-"
                          : "-"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3">
                        {/* Show attached lottery if voucher is linked to one */}
                        {typeof voucher.lottery === "object" && voucher.lottery
                          ? // @ts-ignore
                            voucher.lottery?.en_title ||
                            // @ts-ignore
                            voucher.lottery?.nl_title ||
                            // @ts-ignore
                            voucher.lottery?._id ||
                            "-"
                          : voucher.lottery
                            ? String(voucher.lottery)
                            : "-"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.value || "No value"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 max-w-xs truncate">
                        {voucher.instructions || "No instructions"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3 max-w-xs truncate">
                        {voucher.benefitText || "No benefit text"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.expireDate
                          ? dayjs(voucher.expireDate).format("DD/MM/YYYY")
                          : "No expire date"}
                      </td>
                      <td className="hidden sm:table-cell px-2 sm:px-6 py-2 sm:py-3">
                        {voucher.status}
                      </td>
                      <td
                        className={`px-2 sm:px-6 py-2 sm:py-3 text-right ${
                          Array.isArray(voucher.redeemedBy) &&
                          voucher.redeemedBy.length > 0
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        {Array.isArray(voucher.redeemedBy) &&
                        voucher.redeemedBy.length > 0
                          ? voucher.redeemedBy.length
                          : "Not redeemed"}
                      </td>
                      <td className="px-2 sm:px-6 py-2 sm:py-3 text-right">
                        <TrashIcon
                          className="inline-block cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmDelete = window.confirm(
                              "Are you sure you want to delete this Voucher?"
                            );
                            if (confirmDelete && voucher._id) {
                              deleteVoucher(voucher._id as string);
                            }
                          }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {selectedPartnerId && hasNextPage && (
            <div className="flex justify-end mt-4">
              <PrimaryButton
                label="Load more"
                size="sm"
                onClick={loadMore}
                className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withProtectedRoute(LotteryVouchersScreen);
